import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, Pencil, Plus, QrCode, Tags, X } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { AdminPageHeader, DataTable, exportCsv } from "../../components/admin/DataTable";
import { QrPrintDialog } from "../../components/admin/QrPrintDialog";
import { ArtikelFormDialog } from "../../components/admin/ArtikelFormDialog";
import { KategorienDialog } from "../../components/admin/KategorienDialog";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";

export default function AdminArtikel() {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [positions, setPositions] = useState([]);
    const [compartments, setCompartments] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [editArticle, setEditArticle] = useState(null);
    const [katOpen, setKatOpen] = useState(false);
    // #44: Datenqualität verlinkt mit ?dq=ohne-min | ohne-kategorie hierher.
    // #136: Leitstand-Kacheln verlinken mit ?filter=under-min | low hierher;
    // seit Paket 4 leben ALLE Filter in der URL (Kontexterhalt bei
    // Detail-und-zurück). Fremde Params (q/p der Tabelle) bleiben erhalten.
    const [searchParams, setSearchParams] = useSearchParams();
    const setParam = (key, value) =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value === null || value === "" || value === "all" || value === false) next.delete(key);
            else next.set(key, String(value));
            return next;
        }, { replace: true });
    const dq = searchParams.get("dq") || "";
    const deepFilter = searchParams.get("filter") || "";
    const filterType = searchParams.get("t") || "all";
    const setFilterType = (v) => setParam("t", v);
    const filterLifecycle = searchParams.get("lc") || "all";
    const setFilterLifecycle = (v) => setParam("lc", v);
    const filterCategory = searchParams.get("cat") || "all";
    const setFilterCategory = (v) => setParam("cat", v);
    const onlyCritical = searchParams.get("crit") === "1";
    const setOnlyCritical = (v) => setParam("crit", v ? "1" : null);
    const onlyUnderMin = deepFilter === "under-min";
    const setOnlyUnderMin = (v) => setParam("filter", v ? "under-min" : null);
    const onlyLow = deepFilter === "low";
    const setOnlyLow = (v) => setParam("filter", v ? "low" : null);
    const [qrOpen, setQrOpen] = useState(false);
    const [qrKind, setQrKind] = useState("article"); // 'article' | 'asset'
    const [assets, setAssets] = useState([]);

    const load = () => {
        Promise.all([
            rpc.get("/master/articles"),
            rpc.get("/master/categories"),
            rpc.get("/master/stock-positions"),
            rpc.get("/master/assets"),
            rpc.get("/master/compartments"),
        ]).then(([a, c, p, as, k]) => {
            if (a.ok) setArticles(a.data);
            if (c.ok) setCategories(c.data);
            if (p.ok) setPositions(p.data);
            if (as.ok) setAssets(as.data);
            if (k.ok) setCompartments(k.data);
        });
    };
    useEffect(load, []);

    const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));
    const posByArticle = useMemo(() => {
        const compMap = Object.fromEntries(compartments.map((c) => [c.id, c]));
        const m = {};
        for (const p of positions) {
            m[p.article_id] = m[p.article_id] || { count: 0, qty: 0, parts: [] };
            m[p.article_id].count += 1;
            m[p.article_id].qty += p.quantity || 0;
            m[p.article_id].parts.push({
                qty: p.quantity || 0,
                code: compMap[p.compartment_id]?.code || p.compartment_id?.slice(0, 8),
            });
        }
        return m;
    }, [positions, compartments]);

    // "12 (R1-A-01), 3 (R2-B-05)" -- Menge je Lagerplatz mit lesbarem Fach-Code.
    const stockLabel = (articleId) => {
        const st = posByArticle[articleId];
        if (!st || st.parts.length === 0) return "0";
        return st.parts.map((p) => `${p.qty} (${p.code})`).join(", ");
    };

    const filtered = articles.filter((a) => {
        if (filterType !== "all" && a.type !== filterType) return false;
        if (filterLifecycle !== "all" && a.lifecycle !== filterLifecycle) return false;
        if (filterCategory !== "all" && a.category_id !== filterCategory) return false;
        if (onlyCritical && !a.is_critical) return false;
        if (onlyUnderMin) {
            const st = posByArticle[a.id];
            const minQ = a.min_quantity;
            if (minQ == null) return false;
            if (!st || st.qty > minQ) return false;
        }
        if (onlyLow) {
            // "Niedrig" wie die Leitstand-KPI: über Min, aber <= 1,5 x Min.
            const st = posByArticle[a.id];
            const minQ = a.min_quantity;
            if (minQ == null || !st) return false;
            if (!(st.qty > minQ && st.qty <= 1.5 * minQ)) return false;
        }
        if (dq === "ohne-min" && !(a.min_quantity == null && a.lifecycle === "active")) return false;
        if (dq === "ohne-kategorie" && !(!a.category_id && a.lifecycle === "active")) return false;
        return true;
    });

    const dqLabel = dq === "ohne-min" ? "Artikel ohne Mindestbestand" : dq === "ohne-kategorie" ? "Artikel ohne Kategorie" : null;

    // Zeilen tragen die Gesamtmenge als sortierbares Feld für die Bestand-Spalte.
    const rows = filtered.map((a) => ({ ...a, bestand: posByArticle[a.id]?.qty ?? 0 }));

    // Anzeige bewusst schlank (Wunsch Hauptnutzer 2026-08-10): Name / Bestand / Min;
    // Hersteller-Nr. auf Wunsch vom 2026-08-23 ergänzt (#114).
    // Suche, Filter und CSV-Export behalten den vollen Feldumfang.
    const columns = [
        { key: "name", label: "Name" },
        { key: "manufacturer_number", label: "Hersteller-Nr.", render: (r) => r.manufacturer_number || "—" },
        {
            key: "bestand",
            label: "Bestand",
            render: (r) => stockLabel(r.id),
        },
        { key: "min_quantity", label: "Min" },
        {
            key: "_edit",
            label: "",
            sortable: false,
            render: (r) => (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditArticle(r);
                    }}
                    aria-label={`${r.name} bearbeiten`}
                    className="rounded-md p-1 text-muted-foreground hover:bg-card hover:text-foreground"
                    data-testid={`artikel-row-edit-${r.id}`}
                >
                    <Pencil className="h-4 w-4" />
                </button>
            ),
        },
    ];

    const exportColumns = [
        { key: "name", label: "Name" },
        { key: "bestand", label: "Bestand", export: (r) => stockLabel(r.id) },
        { key: "min_quantity", label: "Min" },
        { key: "type", label: "Typ" },
        {
            key: "category_id",
            label: "Kategorie",
            export: (r) => catMap[r.category_id]?.name || "",
        },
        { key: "ean", label: "EAN" },
        { key: "manufacturer_number", label: "Hersteller-Nr." },
        {
            key: "is_critical",
            label: "Kritisch",
            export: (r) => (r.is_critical ? "ja" : "nein"),
        },
        { key: "lifecycle", label: "Lifecycle" },
        {
            key: "positions",
            label: "Positionen",
            export: (r) => posByArticle[r.id]?.count ?? 0,
        },
    ];

    const exportAll = () =>
        exportCsv(
            `platzwart-artikel-${new Date().toISOString().slice(0, 10)}.csv`,
            rows,
            exportColumns
        );

    return (
        <>
            <AdminPageHeader title="Artikel" subtitle={`${filtered.length} von ${articles.length} Artikeln`}>
                <Button onClick={() => setShowCreate(true)} data-testid="artikel-new">
                    <Plus className="mr-1 h-4 w-4" /> Neuer Artikel
                </Button>
                <Button variant="outline" onClick={() => { setQrKind("article"); setQrOpen(true); }} data-testid="artikel-qr-article" disabled={filtered.length === 0}>
                    <QrCode className="mr-1 h-4 w-4" /> Artikel-QR
                </Button>
                <Button variant="outline" onClick={() => { setQrKind("asset"); setQrOpen(true); }} data-testid="artikel-qr-asset" disabled={assets.length === 0}>
                    <QrCode className="mr-1 h-4 w-4" /> QR für Ausleihgegenstände
                </Button>
                <Button variant="outline" onClick={() => setKatOpen(true)} data-testid="artikel-kategorien">
                    <Tags className="mr-1 h-4 w-4" /> Kategorien
                </Button>
                <Button variant="outline" onClick={exportAll} data-testid="artikel-export">
                    <Download className="mr-1 h-4 w-4" /> CSV Export
                </Button>
            </AdminPageHeader>

            {dqLabel ? (
                <div
                    className="mb-3 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs"
                    data-testid="artikel-dq-filter"
                >
                    <span className="flex-1">
                        Datenqualität: <span className="font-medium">{dqLabel}</span> — {filtered.length} Einträge, je Zeile über den Stift direkt bearbeiten.
                    </span>
                    <button
                        type="button"
                        onClick={() => setParam("dq", null)}
                        aria-label="Datenqualitäts-Filter aufheben"
                        className="rounded-md border border-border px-2 py-1"
                        data-testid="artikel-dq-filter-clear"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            ) : null}

            <DataTable
                testId="admin-artikel-table"
                columns={columns}
                rows={rows}
                rowKey={(r) => r.id}
                onRowClick={(r) => navigate(`/admin/artikel/${r.id}`)}
                search={(r) => `${r.name} ${r.ean || ""} ${r.manufacturer_number || ""}`}
                filtersSlot={
                    <>
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger className="h-10 w-36" data-testid="artikel-filter-type">
                                <SelectValue placeholder="Typ" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">alle Typen</SelectItem>
                                <SelectItem value="bulk">bulk</SelectItem>
                                <SelectItem value="single">single</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterLifecycle} onValueChange={setFilterLifecycle}>
                            <SelectTrigger className="h-10 w-36" data-testid="artikel-filter-lifecycle">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Lifecycle: alle</SelectItem>
                                <SelectItem value="active">aktiv</SelectItem>
                                <SelectItem value="archived">archiviert</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="h-10 w-44" data-testid="artikel-filter-category">
                                <SelectValue placeholder="Kategorie" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">alle Kategorien</SelectItem>
                                {categories.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Checkbox
                                checked={onlyCritical}
                                onCheckedChange={setOnlyCritical}
                                data-testid="artikel-filter-critical"
                            />
                            Nur kritisch
                        </label>
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Checkbox
                                checked={onlyUnderMin}
                                onCheckedChange={setOnlyUnderMin}
                                data-testid="artikel-filter-under-min"
                            />
                            Nur unter Min
                        </label>
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Checkbox
                                checked={onlyLow}
                                onCheckedChange={setOnlyLow}
                                data-testid="artikel-filter-low"
                            />
                            Nur niedrig (≤ 1,5×Min)
                        </label>
                    </>
                }
            />

            <ArtikelFormDialog
                open={showCreate}
                onClose={() => setShowCreate(false)}
                categories={categories}
                onSaved={() => {
                    setShowCreate(false);
                    load();
                }}
            />

            <ArtikelFormDialog
                open={!!editArticle}
                onClose={() => setEditArticle(null)}
                categories={categories}
                article={editArticle}
                onSaved={() => {
                    setEditArticle(null);
                    load();
                }}
            />

            <KategorienDialog
                open={katOpen}
                onClose={() => setKatOpen(false)}
                categories={categories}
                onChanged={() => rpc.get("/master/categories").then((c) => c.ok && setCategories(c.data))}
            />

            <QrPrintDialog
                open={qrOpen}
                onClose={() => setQrOpen(false)}
                title={qrKind === "asset" ? "QR-Codes für Ausleihgegenstände drucken" : "Artikel-QR-Codes drucken"}
                kind={qrKind}
                items={
                    qrKind === "asset"
                        ? assets.map((a) => {
                              const art = articles.find((x) => x.id === a.article_id);
                              return {
                                  id: a.id,
                                  title: a.asset_tag || a.serial_number || a.id.slice(0, 8),
                                  subtitle: [art?.name, a.status, a.lifecycle].filter(Boolean).join(" · "),
                              };
                          })
                        : filtered.map((a) => ({
                              id: a.id,
                              title: a.name,
                              subtitle: [a.ean, catMap[a.category_id]?.name, a.type].filter(Boolean).join(" · "),
                          }))
                }
                testId="artikel-qr-dialog"
            />
        </>
    );
}
